import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Landmark, Loader2, QrCode, ImagePlus, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { qrSrc } from "@/lib/bank";

const fmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

const Row = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast(value); }
  };
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-200 py-2.5 last:border-0">
      <div className="min-w-0">
        <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
        <p data-testid={testid} className="truncate font-mono2 text-sm text-slate-900">{value}</p>
      </div>
      <button type="button" data-testid={`${testid}-copy`} onClick={copy} aria-label={`Copy ${label}`} className="shrink-0 border border-slate-200 p-2 text-slate-500 transition-colors hover:border-[#6D28D9]/50 hover:text-[#6D28D9]">
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
};

// order = response of POST /api/orders/manual (includes .bank)
export const BankTransferPanel = ({ order, onReported }) => {
  const [utr, setUtr] = useState("");
  const [proof, setProof] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const b = order.bank;

  const pickProof = (file) => {
    if (preview) URL.revokeObjectURL(preview);
    if (!file) { setProof(null); setPreview(null); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Screenshot must be 5 MB or smaller"); return; }
    setProof(file);
    setPreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let proof_path = null;
      if (proof) {
        const fd = new FormData();
        fd.append("file", proof);
        proof_path = (await api.post(`/orders/${order.id}/proof`, fd)).data.proof_path;
      }
      const res = await api.post(`/orders/${order.id}/utr`, { utr });
      setDone({ ...res.data, proof_path });
      toast.success("Thanks! The desk will verify your transfer and confirm the order.");
      onReported?.(res.data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="bank-transfer-panel" className="mt-6 border border-[#6D28D9]/25 bg-white p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#6D28D9]"><Landmark className="h-3.5 w-3.5" /> Pay by bank transfer / UPI</p>
          <p className="mt-2 text-sm text-slate-600">Transfer <span className="font-semibold text-slate-900">₹{fmt.format(order.amount)}</span> and mention reference <span data-testid="bank-order-ref" className="font-mono2 font-semibold text-[#6D28D9]">{order.reference}</span> in the remarks.</p>
        </div>
        <span className="border border-amber-300 bg-amber-50 px-3 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.18em] text-amber-700">Order reserved · awaiting payment</span>
      </div>

      <div className="mt-5 grid gap-6 md:grid-cols-[1fr_auto]">
        <div>
          <Row label="Account holder" value={b.account_name} testid="bank-account-name" />
          <Row label="Bank" value={b.bank_name} testid="bank-name" />
          <Row label="Account number" value={b.account_number} testid="bank-account-number" />
          <Row label="IFSC" value={b.ifsc} testid="bank-ifsc" />
          <Row label="UPI ID" value={b.upi_id} testid="bank-upi-id" />
          <Row label="Amount" value={`₹${fmt.format(order.amount)}`} testid="bank-amount" />
        </div>
        <div className="flex flex-col items-center">
          <p className="mb-2 flex items-center gap-1.5 font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500"><QrCode className="h-3.5 w-3.5" /> Scan with any UPI app</p>
          <img data-testid="bank-upi-qr" src={qrSrc(b.upi_qr)} alt="UPI QR code" className="w-48 border border-slate-200 bg-white p-1 sm:w-56" />
        </div>
      </div>

      {done ? (
        <p data-testid="bank-utr-done" className="mt-5 flex items-center gap-2 border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"><Check className="h-4 w-4" /> Payment reported with UTR {done.utr}{done.proof_path ? " and your screenshot" : ""}. You'll get a receipt once the desk confirms — track it under My Account → Portfolio.</p>
      ) : (
        <form data-testid="bank-utr-form" onSubmit={submit} className="mt-5 border-t border-slate-200 pt-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="flex-1">
              <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">After paying, enter the UTR / transaction ID</span>
              <input data-testid="bank-utr-input" required minLength={6} value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. 425612345678" className="mt-1.5 w-full border border-slate-200 bg-white px-4 py-3 font-mono2 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-[#6D28D9]/60" />
            </label>
            <button data-testid="bank-utr-submit" type="submit" disabled={busy || utr.trim().length < 6} className="flex items-center justify-center gap-2 bg-[#6D28D9] px-6 py-3.5 font-mono2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#5B21B6] disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "I have paid"}
            </button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {preview ? (
              <div data-testid="bank-proof-preview" className="flex items-center gap-3 border border-slate-200 bg-white p-2">
                <img src={preview} alt="Payment screenshot" className="h-14 w-14 object-cover" />
                <span className="max-w-[160px] truncate text-xs text-slate-600">{proof.name}</span>
                <button type="button" data-testid="bank-proof-remove" onClick={() => pickProof(null)} aria-label="Remove screenshot" className="p-1 text-slate-400 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <label data-testid="bank-proof-upload" className="flex cursor-pointer items-center gap-2 border border-dashed border-[#6D28D9]/40 px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#6D28D9] transition-colors hover:bg-violet-50">
                <ImagePlus className="h-3.5 w-3.5" /> Attach payment screenshot (optional)
                <input data-testid="bank-proof-input" type="file" accept="image/*" className="hidden" onChange={(e) => pickProof(e.target.files?.[0])} />
              </label>
            )}
            <span className="text-[11px] text-slate-400">A screenshot helps the desk verify faster.</span>
          </div>
        </form>
      )}
    </div>
  );
};
